// components/chat/renderUserMentions.tsx
import React from 'react';
import { OpenAIFolderIcon as Folder } from '@/components/ui/OpenAIFolderIcon';
import { factoryFolderMentions } from '#lib/list/factoryFolderMentions';
import { factoryImageMentions } from '#lib/list/factoryImageMentions';

const tokens = /(?<tildes>~{3,})[\s\S]*?(?:\k<tildes>|$)|(?<ticks>`+)[\s\S]*?(?:\k<ticks>|$)|(?<linkPrefix>!?\[)(?<linkLabel>(?:[^[\]\n]|\[[^\]\n]*\])*)\](?<linkTarget>\([^)\n]*\)|\[[^\]\n]*\])|https?:\/\/[^\s]+|(?<prefix>^|[\s(*_])(?<mention>@(?:output:[a-z0-9-]+(?:\[(?<last>last:)?(?<outputNumber>[1-9]\d*)\])?|object:[a-z0-9](?:[a-z0-9-]{0,46}[a-z0-9])?|(?!object:|output:)[a-z0-9](?:[a-z0-9-]{0,46}[a-z0-9])?))(?!:[a-z0-9])(?=$|[\s),.;:!?*_])/gi;
const mentionLabel = (groups: Record<string, string | undefined>) => groups.last ? `@last (Output ${groups.outputNumber})`
  : groups.outputNumber ? `@Output ${groups.outputNumber}` : /^@output:/i.test(groups.mention ?? '') ? '@Output' : groups.mention!;

export function formatOutputMentions(text: string): string {
  let formatted = text.replace(tokens, (...args) => {
    const groups = args.at(-1) as Record<string, string | undefined>;
    if (groups.linkLabel !== undefined) return groups.linkPrefix + formatOutputMentions(groups.linkLabel) + ']' + groups.linkTarget;
    return groups.mention ? (groups.prefix ?? '') + mentionLabel(groups) : args[0];
  });
  for (const folder of [...factoryFolderMentions(formatted), ...factoryImageMentions(formatted)].sort((a, b) => b.start - a.start)) {
    if (folder.name !== undefined) formatted = formatted.slice(0, folder.start) + `📁 ${folder.name}` + formatted.slice(folder.start + folder.token.length);
  }
  return formatted;
}

export function createDisplayNameFormatter(names: Record<string, string> = {}) {
  const identifiers = Object.keys(names).filter(Boolean).sort((a, b) => b.length - a.length).map(id => id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const identifierPattern = new RegExp(`(?<![\\w-])(?:${[...identifiers, 'factory-preset-[a-z0-9-]+', 'image-[a-f0-9]{8}-[a-f0-9-]{27,}'].join('|')})(?![\\w-])`, 'g');
  return (value: string) => value.replace(identifierPattern, id => names[id] ?? (id.startsWith('image-') ? 'reference image' : 'Factory folder'));
}

/** Visit prose after Markdown parsing; link destinations keep canonical identities. */
export function remarkOutputMentions(options?: { names?: Record<string, string> }) {
  type Node = { type: string; value?: string; children?: Node[] };
  const displayNames = createDisplayNameFormatter(options?.names);
  return function visit(node: Node): void {
    if (node.type === 'text' && typeof node.value === 'string') node.value = displayNames(formatOutputMentions(node.value));
    if (node.type === 'inlineCode' && typeof node.value === 'string') node.value = displayNames(node.value);
    node.children?.forEach(visit);
  };
}

/** Output labels are presentation only: the stored token retains its immutable ID. */
export function renderUserMentions(text: string, onReference?: (folderId: string, imageId?: string) => void): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let end = 0;
  const mentions: { token: string; start: number; label: string; folderId?: string; imageId?: string }[] = [...text.matchAll(tokens)].flatMap(match => match.groups?.mention
    ? [{ token: match.groups.mention, start: match.index + (match.groups.prefix?.length ?? 0), label: mentionLabel(match.groups) }] : []);
  mentions.push(...factoryFolderMentions(text).filter(folder => folder.name !== undefined).map(folder => ({ ...folder, label: folder.name!, folderId: folder.id })));
  mentions.push(...factoryImageMentions(text).map(image => ({ ...image, label: image.name })));
  for (const mention of mentions.sort((a, b) => a.start - b.start)) {
    const { start, label } = mention;
    const Chip = onReference ? 'button' : 'span';
    parts.push(text.slice(end, start), mention.folderId
      ? <Chip key={start} {...(onReference ? { type: 'button' as const, onClick: () => onReference(mention.folderId!, mention.imageId), 'aria-label': `Open ${label}` } : {})} data-factory-folder-mention className="inline-flex max-w-full items-center gap-1 rounded-md bg-[#293342] px-1.5 align-middle text-[#B5CFFF] focus-visible:outline focus-visible:outline-2"><Folder aria-hidden="true" className="h-3.5 w-3.5 shrink-0" /><span>{label}</span></Chip>
      // Inherit the message's type: globals.css pins every span to 12px, which printed the mention
      // smaller than the words around it (owner, 2026-09-29).
      : <span key={start} style={{ color: '#3B82F6', fontSize: 'inherit', lineHeight: 'inherit' }}>{label}</span>);
    end = start + mention.token.length;
  }
  return end ? [...parts, text.slice(end)] : text;
}
